import { useTranslation } from "react-i18next";
import { Label, Input, Checkbox, cn } from "@tellescope/ui";
import type { ProfileEditBlock, ProfileFieldKey, ProfileFieldConfig } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import type { BlockTypeEditorProps } from "../types";
import { getProfileFieldLabels, PROFILE_FIELD_KEYS, NON_REQUIRED_BY_DEFAULT } from "../constants";

type ProfileEditBlockEditorProps = BlockTypeEditorProps<ProfileEditBlock>;

export function ProfileEditBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: ProfileEditBlockEditorProps) {
  const { t } = useTranslation();
  const fieldConfigs = block.props.fields ?? {};

  // Get config for a field with defaults
  const getFieldConfig = (key: ProfileFieldKey) => {
    const config = fieldConfigs[key];
    const defaultRequired = !NON_REQUIRED_BY_DEFAULT.includes(key);
    return {
      visible: config?.visible ?? true,
      editable: config?.editable ?? true,
      required: config?.required ?? defaultRequired,
      label: config?.label ?? "",
    };
  };

  // Update a single field's configuration
  const updateFieldConfig = (
    key: ProfileFieldKey,
    updates: { visible?: boolean; editable?: boolean; required?: boolean; label?: string }
  ) => {
    const currentConfig = getFieldConfig(key);
    const newConfig = { ...currentConfig, ...updates };
    const defaultRequired = !NON_REQUIRED_BY_DEFAULT.includes(key);

    // Only store non-default values to keep the config clean
    const storedConfig: Partial<ProfileFieldConfig> = {};
    if (!newConfig.visible) storedConfig.visible = false;
    if (!newConfig.editable) storedConfig.editable = false;
    // Store required only if it differs from the field's default
    if (newConfig.required !== defaultRequired) storedConfig.required = newConfig.required;
    if (newConfig.label) storedConfig.label = newConfig.label;

    const newFields = { ...fieldConfigs };
    if (Object.keys(storedConfig).length > 0) {
      newFields[key] = storedConfig;
    } else {
      delete newFields[key];
    }

    updateProps({ fields: newFields });
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label>{t("Title (optional)")}</Label>
        <Input
          value={block.props.title || ""}
          onChange={(e) => updateProps({ title: e.target.value || undefined })}
          placeholder={t("Edit Profile")}
        />
      </div>

      {/* Submit button text */}
      <div className="space-y-2">
        <Label>{t("Submit Button Text")}</Label>
        <Input
          value={block.props.submitButtonText || "Save Changes"}
          onChange={(e) => updateProps({ submitButtonText: e.target.value || "Save Changes" })}
        />
      </div>

      {/* Success message */}
      <div className="space-y-2">
        <Label>{t("Success Message")}</Label>
        <Input
          value={block.props.successMessage || "Profile updated successfully"}
          onChange={(e) => updateProps({ successMessage: e.target.value || "Profile updated successfully" })}
        />
      </div>

      {/* Show reset button */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="showResetButton"
          checked={block.props.showResetButton ?? true}
          onCheckedChange={(checked) => updateProps({ showResetButton: !!checked })}
        />
        <label htmlFor="showResetButton" className="text-sm cursor-pointer">
          {t("Show Reset Button")}
        </label>
      </div>

      {/* Field configuration */}
      <div className="space-y-2 pt-2 border-t">
        <Label>{t("Field Configuration")}</Label>
        <p className="text-xs text-muted-foreground mb-3">{t("Configure which fields to show and their settings.")}</p>

        <div className="space-y-2">
          {PROFILE_FIELD_KEYS.map((key) => {
            const config = getFieldConfig(key);
            return (
              <div key={key} className="p-2 rounded border bg-muted/30">
                <div className="flex items-center gap-3 mb-2">
                  <Checkbox
                    id={`field-visible-${key}`}
                    checked={config.visible}
                    onCheckedChange={(checked) => updateFieldConfig(key, { visible: !!checked })}
                  />
                  <label htmlFor={`field-visible-${key}`} className="text-sm font-medium flex-1 cursor-pointer">
                    {getProfileFieldLabels()[key]}
                  </label>
                </div>

                {config.visible && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`field-editable-${key}`}
                          checked={config.editable}
                          onCheckedChange={(checked) => updateFieldConfig(key, { editable: !!checked })}
                        />
                        <label
                          htmlFor={`field-editable-${key}`}
                          className="text-xs text-muted-foreground cursor-pointer"
                        >
                          {t("Editable")}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`field-required-${key}`}
                          checked={config.required}
                          onCheckedChange={(checked) => updateFieldConfig(key, { required: !!checked })}
                          disabled={!config.editable}
                        />
                        <label
                          htmlFor={`field-required-${key}`}
                          className={cn(
                            "text-xs text-muted-foreground cursor-pointer",
                            !config.editable && "opacity-50"
                          )}
                        >
                          {t("Required")}
                        </label>
                      </div>
                    </div>
                    <Input
                      value={config.label}
                      onChange={(e) => updateFieldConfig(key, { label: e.target.value })}
                      placeholder={getProfileFieldLabels()[key]}
                      className="h-7 text-xs"
                    />
                    <p className="text-xs text-muted-foreground">{t("Custom label (optional)")}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
